import { Config } from "@/shared/config";
import {
    ExpenseEligibilityGroup,
    HsabExpenseByExternalIdRequest,
    ManagedExpenseFilterType
} from "@/shared/types";

const expenseServiceUri = Config.ExpenseServicebaseUri;
const expenseV1 = `${expenseServiceUri}/expense/v1`;
export default {
    get expenseV1 () {
        return `${expenseServiceUri}/expense/v1`;
    },
    get expenseV2 () {
        return `${expenseServiceUri}/expense/v2`;
    },
    getLinkedCards () {
        return `${this.expenseV1}/linkedcards`;
    },
    getExpenses (page: number, pageSize: number, eligibilityGroup: ExpenseEligibilityGroup) {
        return `${this.expenseV1}/transactions/pending/list?page=${page}` +
            `&pageSize=${pageSize}&eligibilityGroupId=${eligibilityGroup}`;
    },
    getExpensesNew () {
        return `${this.expenseV1}/transactions/expenses/list`;
    },
    searchExpenses () {
        return `${this.expenseV1}/expenses`;
    },
    putEligibilityStatus () {
        return `${this.expenseV1}/expenses/eligibilitystatus`;
    },
    getBalanceSummary () {
        return `${this.expenseV1}/balancesummary`;
    },
    expenseEdit (expenseId?: number) {
        return `${this.expenseV1}/expenses${expenseId ? "/" + expenseId : ""}`;
    },
    expenseEvidence (expenseId: number, filename?: string) {
        if (filename) {
            return `${this.expenseV1}/expense-evidence/${expenseId}/${filename}`;
        }

        return `${this.expenseV1}/expense-evidence/${expenseId}`;
    },
    expenseEvidenceS3PreSignedUrl (expenseId: number, filename?: string) {
        return `${this.expenseV1}/expense-evidence/download-link/${expenseId}/${filename}`;
    },
    getAvailableHsabExpenses (page: number, pageSize: number) {
        return `${this.expenseV2}/expenses/available?page=${page}&pageSize=${pageSize}`;
    },
    getManagedExpensesPage (page: number, pageSize: number, filter: ManagedExpenseFilterType) {
        return `${this.expenseV2}/managed-expenses?page=${page}&pageSize=${pageSize}&filter=${filter}`;
    },
    getUserHasHealthPlanExpense () {
        return `${this.expenseV2}/managed-expenses/has-health-plan-expense`;
    },
    getSubmittedHsabExpenses (page: number, pageSize: number) {
        return `${this.expenseV2}/expenses/submitted?page=${page}&pageSize=${pageSize}`;
    },
    createOrUpdateHsabExpense () {
        return `${this.expenseV2}/expenses/create-or-update`;
    },
    submitHsabExpense (expenseId: number) {
        return `${this.expenseV2}/expenses/${expenseId}/submit`;
    },
    submitHsabExpenseEvidence (expenseId: number) {
        return `${this.expenseV2}/expenses/${expenseId}/submit-evidence`;
    },
    syncHsabExpenses () {
        return `${this.expenseV2}/expenses/sync`;
    },
    getHsabExpenseCategories () {
        return `${this.expenseV2}/expenses/categories`;
    },
    DeleteHsabExpense (expenseId: number) {
        return `${this.expenseV2}/expenses/${expenseId}`;
    },
    getHsabExpenseById (expenseId: number) {
        return `${this.expenseV2}/expenses/${expenseId}`;
    },
    getHsabExpenseByExternalId (request: HsabExpenseByExternalIdRequest) {
        let url = `${this.expenseV2}/expenses/expense-by-external-id?`;
        if (request.externalReferenceId) {
            url += "externalReferenceId=" + request.externalReferenceId;
        } else if (request.externalReimbursementId) {
            url += "externalReimbursementId=" + request.externalReimbursementId;
        } else if (request.externalTransactionId) {
            url += "externalTransactionId=" + request.externalTransactionId;
        }
        return url;
    },
    reimbursement () {
        return `${this.expenseV1}/reimbursement`;
    },
    unreimbursed (page: number, pageSize: number, receipts) {
        return `${this.expenseV1}/expenses/unreimbursed?page=${page}&pageSize=${pageSize}&receipts=${receipts}`;
    },
    unreimbursedCount: () => `${expenseV1}/expenses/unreimbursedCount`,
    unreimbursedWithoutAttachmentsCount: () => `${expenseV1}/expenses/unreimbursedNoReceiptsCount`,
    reimbursed (page: number, pageSize: number, excludeFailures: boolean) {
        return `${this.expenseV1}/reimbursement?page=${page}&pageSize=${pageSize}&excludeFailures=${excludeFailures}`;
    },
    reimbursedExpensesByReimbursementId (reimbursementId: number) {
        return `${this.expenseV1}/reimbursement/${reimbursementId}`;
    },
    reimbursedExpensesByBankTransactionIds (bankTransactionIds: string) {
        return `${this.expenseV1}/reimbursement/banktransactions/${bankTransactionIds}`;
    },
    unreimbursedGraph (startDate: string, endDate: string) {
        return `${this.expenseV1}/expenses/unreimbursedbalances?startDate=${startDate}&endDate=${endDate}`;
    },
    reimbursedMonthlySummary (startDate: string, endDate: string) {
        return `${this.expenseV1}/reimbursement/monthly-totals?startDate=${startDate}&endDate=${endDate}`;
    },
    createReimbursementRequest () {
        return `${this.expenseV1}/reimbursement/reimburserequest`;
    },
    updateReimbursementRequest (reimbursementRequestId: number) {
        return `${this.expenseV1}/reimbursement/reimburserequest/${reimbursementRequestId}`;
    },
    expenseAmountGroupByCategory (year?: number) {
        return `${this.expenseV1}/expenses/amount-by-category/${year || ""}`;
    },
    getImportedExpenses () {
        return `${this.expenseV1}/imported-expense`;
    },
    importedExpenseEvidenceDownload (reimbursedExpenseId: number, fileName: string) {
        return `${this.expenseV1}/imported-expense/download-link/${reimbursedExpenseId}/${fileName}`;
    },
    manualMarkedAsReimbursed () {
        return `${this.expenseV1}/reimbursement/manual-mark-reimbursed`;
    },
    getExpensesByIds () {
        return `${this.expenseV1}/expenses/expenses-by-ids`;
    },
    deleteExpense (expenseId: number) {
        return `${this.expenseV1}/expenses/${expenseId}`;
    },
    getAdminFinicityLinkedAccounts (cognitoUsername: string) {
        return `${this.expenseV1}/admin-finicity-linked-accounts?cognitoUsername=${cognitoUsername}`;
    },
    getPayeesForUser () {
        return `${this.expenseV1}/payees`;
    },
    createPayee () {
        return `${this.expenseV1}/payees`;
    },
    updatePayee () {
        return `${this.expenseV1}/payees`;
    },
    deletePayee (payeeId: number) {
        return `${this.expenseV1}/payees/${payeeId}`;
    },
    getExpenseEvidence (expenseId: number) {
        return `${this.expenseV2}/expenses/${expenseId}/evidence`;
    },
    deleteExpenseEvidence (expenseId: number, expenseEvidenceId: number) {
        return `${this.expenseV2}/expenses/${expenseId}/evidence/${expenseEvidenceId}`;
    },
    retrySubmitHsabExpenseEvidence (expenseId: number, expenseEvidenceId: number) {
        return `${this.expenseV2}/expenses/${expenseId}/evidence/${expenseEvidenceId}/submit`;
    },
    uploadFilesToHsabExpense (expenseId: number) {
        return `${this.expenseV2}/expenses/${expenseId}/evidence`;
    },
    getHsabExpenseEvidenceSignedS3Url (expenseId: number, expenseEvidenceId: number) {
        return `${this.expenseV2}/expenses/${expenseId}/evidence/${expenseEvidenceId}/download-url`;
    },
    getExpenseByExternalId (externalId: string) {
        return `${this.expenseV1}/expenses/expense-by-external-id/${externalId}`;
    },
    submitExpenseToWex (expenseId: number) {
        return `${this.expenseV1}/expenses/${expenseId}/submit-to-wex`;
    },
    syncWexExpenses () {
        return `${this.expenseV1}/expenses/sync-wex-expenses`;
    },
    submitExpenseEvidenceToWex (expenseId: number) {
        return `${this.expenseV1}/expenses/${expenseId}/submit-evidence-to-wex`;
    },
    getConsumerBankAccounts () {
        return `${this.expenseV2}/consumer-bank-accounts/bank-accounts`;
    },
};
